import {
  Alert,
  Autocomplete,
  Box,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import { UserContext } from '../../../contexts/userContext';
import { UserType } from '../../../interfaces/UserTypes';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import axiosConfig from '../../../Resources/axiosConfig';

import { useLocation, useNavigate } from 'react-router-dom';

interface InformationProps {
  values: I_Installation;
  s3Credentials: I_S3Credentials;
  type?: string;
  // LAN address the device reports up in its config payload (values.Config.LocalIp).
  // Read-only, device-reported — not persisted on the Installation record.
  localIp?: string;
  // True once a device record has actually been read from S3. Separates "the device
  // does not report a Local IP" from "we have not managed to read the device yet" —
  // both leave localIp undefined, but they mean very different things to the user.
  deviceDataAvailable?: boolean;
  // True once the Information tab's one-shot read has finished, hit or miss. Without it
  // a finished-but-empty probe is indistinguishable from one still in flight, and the
  // field would sit on "Waiting for device data" forever.
  deviceProbeSettled?: boolean;
}

function Information(props: InformationProps) {
  if (props.values === null) {
    return null;
  }

  const context = useContext(UserContext);
  const { currentUser } = context;
  const theme = useTheme();
  const [formValues, setFormValues] = useState(props.values);
  const requiredFields = ['name', 'region', 'location', 'country'];
  const installationContext = useContext(InstallationsContext);
  const { product, setProduct } = useContext(ProductIdContext);
  const {
    updateInstallation,
    loading,
    setLoading,
    error,
    setError,
    updated,
    setUpdated,
    deleteInstallation
  } = installationContext;

  const canEdit = currentUser.userType == UserType.admin;
  const isPartner = currentUser.userType == UserType.partner;
  const isSodistore = formValues.product === 3 || formValues.product === 4 || formValues.product === 5;
  // Device-reported Local IP: SodistoreMax (3) and SodistoreGrid (4) only.
  // Explicit allow-list — Salimax (0) firmware does not report Config.LocalIp.
  const showLocalIp = formValues.product === 3 || formValues.product === 4;

  const [networkProviders, setNetworkProviders] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  useEffect(() => {
    if (isSodistore) {
      setLoadingProviders(true);
      axiosConfig
        .get('/GetNetworkProviders')
        .then((res) => setNetworkProviders(res.data))
        .catch(() => {})
        .finally(() => setLoadingProviders(false));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  const handleSubmit = () => {
    setLoading(true);
    setError(false);
    updateInstallation(formValues, props.type);
  };

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    return true;
  };

  const [openModalDeleteInstallation, setOpenModalDeleteInstallation] =
    useState(false);

  const handleDelete = () => {
    setLoading(true);
    setError(false);
    setOpenModalDeleteInstallation(true);
  };

  const location = useLocation();
  const navigate = useNavigate();

  const deleteInstallationModalHandle = () => {
    setOpenModalDeleteInstallation(false);
    deleteInstallation(formValues, props.type);
    setLoading(false);

    const pathSegments = location.pathname.split('/'); // e.g. ['', 'sodiohome_installations', 'list', 'installation', '123', 'information']

    const base = `/${pathSegments[1]}`;
    const isListView = pathSegments.includes('list');
    const isTreeView = pathSegments.includes('tree');

    if (isListView) {
      navigate(`${base}/list/`);
    } else if (isTreeView) {
      navigate(`${base}/tree/`);
    } else {
      // fallback
      navigate(`${base}/list/`);
    }
  };

  const deleteInstallationModalHandleCancel = () => {
    setOpenModalDeleteInstallation(false);
    setLoading(false);
  };

  return (
    <>
      {openModalDeleteInstallation && (
        <Modal
          open={openModalDeleteInstallation}
          onClose={deleteInstallationModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage
                id="confirmDeleteInstallation"
                defaultMessage="Do you want to delete this installation?"
              />
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                userSelect: 'all',
                width: '100%',
                textAlign: 'center'
              }}
            >
              {props.s3Credentials.s3Bucket}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center' }}
            >
              <FormattedMessage
                id="deleteInstallationWarning"
                defaultMessage="Please note the bucket name above. Purging S3 data may take several minutes. If deletion fails, you can manually remove the bucket from Exoscale."
              />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandle}
              >
                Delete
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandleCancel}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
      )}
      <Container maxWidth="xl">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} md={12}>
            <CardContent>
              <Box
                component="form"
                sx={{
                  '& .MuiTextField-root': {
                    m: 1,
                    width: { xs: 'calc(100% - 16px)', md: '50ch' }
                  }
                }}
                noValidate
                autoComplete="off"
              >
                <div>
                  <TextField
                    label={<FormattedMessage id="name" defaultMessage="name" />}
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={formValues.name === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="region" defaultMessage="Region" />
                    }
                    name="region"
                    value={formValues.region}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.region === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="location"
                        defaultMessage="Location"
                      />
                    }
                    name="location"
                    value={formValues.location}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.location === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="country" defaultMessage="Country" />
                    }
                    name="country"
                    value={formValues.country}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.country === ''}
                  />
                </div>
                {isSodistore && (
                  <div>
                    <Autocomplete
                      freeSolo
                      options={networkProviders}
                      value={formValues.networkProvider || ''}
                      onChange={(_e, val) =>
                        setFormValues({
                          ...formValues,
                          networkProvider: (val as string) || ''
                        })
                      }
                      onInputChange={(_e, val) =>
                        setFormValues({
                          ...formValues,
                          networkProvider: val || ''
                        })
                      }
                      disabled={!canEdit && !isPartner}
                      loading={loadingProviders}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <FormattedMessage
                              id="networkProvider"
                              defaultMessage="Network Provider"
                            />
                          }
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingProviders ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </div>
                )}

                {/* Device-reported LAN address — read-only for every role (admin, partner, client) */}
                {showLocalIp && (
                  <div>
                    <TextField
                      label={
                        <FormattedMessage
                          id="localIp"
                          defaultMessage="Local IP"
                        />
                      }
                      value={props.localIp || ''}
                      variant="outlined"
                      fullWidth
                      inputProps={{ readOnly: true }}
                      helperText={
                        props.localIp ? (
                          <FormattedMessage
                            id="localIpHelper"
                            defaultMessage="LAN address reported by the device"
                          />
                        ) : props.deviceDataAvailable ? (
                          <FormattedMessage
                            id="localIpNotReported"
                            defaultMessage="Not reported by this device"
                          />
                        ) : props.deviceProbeSettled ? (
                          <FormattedMessage
                            id="localIpUnavailable"
                            defaultMessage="No device data available"
                          />
                        ) : (
                          <FormattedMessage
                            id="localIpWaiting"
                            defaultMessage="Waiting for device data"
                          />
                        )
                      }
                    />
                  </div>
                )}

                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="orderNumbers"
                        defaultMessage="Order Numbers"
                      />
                    }
                    name="orderNumbers"
                    value={formValues.orderNumbers}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="installation_name"
                        defaultMessage="Installation Name"
                      />
                    }
                    name="installationName"
                    value={formValues.installationName}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="information"
                        defaultMessage="Information"
                      />
                    }
                    name="information"
                    value={formValues.information}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={6} // 👈 Makes it visually bigger
                    maxRows={12} // 👈 Optional max height before scroll
                    inputProps={{
                      style: {
                        fontFamily: 'monospace' // optional: makes tabs/formatting more clear
                      }
                    }}
                  />
                </div>

                {canEdit && (
                  <>
                    <div>
                      <TextField
                        label="Vpn IP"
                        name="vpnIp"
                        value={formValues.vpnIp}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Bucket Name"
                        name="s3bucketname"
                        value={
                          formValues.product === 0 || formValues.product === 3
                            ? formValues.s3BucketId +
                              '-3e5b3069-214a-43ee-8d85-57d72000c19d'
                            : formValues.product === 4
                            ? formValues.s3BucketId +
                              '-5109c126-e141-43ab-8658-f3c44c838ae8'
                            : formValues.product === 5
                            ? formValues.s3BucketId +
                              '-325c9373-9025-4a8d-bf5a-f9eedf1f155c'
                            : formValues.product === 1
                            ? formValues.s3BucketId +
                              '-c0436b6a-d276-4cd8-9c44-1eae86cf5d0e'
                            : formValues.s3BucketId +
                              '-e7b9a240-3c5d-4d2e-a019-6d8b1f7b73fa'
                        }
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <TextField
                        label="S3 Write Key"
                        name="s3writekey"
                        value={formValues.s3WriteKey}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Write Secret Key"
                        name="s3writesecretkey"
                        value={formValues.s3WriteSecret}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 10,
                    // Phones: wrap buttons instead of overflowing (German labels).
                    // No-op on desktop (single row → no wrap, no cross-axis gap).
                    flexWrap: 'wrap',
                    rowGap: 8
                  }}
                >
                  {canEdit && (
                    <Button
                      variant="contained"
                      onClick={handleDelete}
                      sx={{
                        marginLeft: '10px'
                      }}
                    >
                      <FormattedMessage
                        id="deleteInstallation"
                        defaultMessage="Delete Installation"
                      />
                    </Button>
                  )}
                  {(canEdit || (isPartner && isSodistore)) && (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        marginLeft: '10px'
                      }}
                      disabled={!areRequiredFieldsFilled()}
                    >
                      <FormattedMessage
                        id="applyChanges"
                        defaultMessage="Apply Changes"
                      />
                    </Button>
                  )}

                  {loading && (
                    <CircularProgress
                      sx={{
                        color: theme.palette.primary.main,
                        marginLeft: '20px'
                      }}
                    />
                  )}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="errorOccured"
                        defaultMessage="An error has occurred"
                      />
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setError(false)}
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                  {updated && (
                    <Alert
                      severity="success"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="successfullyUpdated"
                        defaultMessage="Successfully updated"
                      />

                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setUpdated(false)} // Set error state to false on click
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                </div>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default Information;
